import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export interface Part {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  part_number: string | null;
  barcode: string | null;
  unit_cost: number | null;
  qty_in_stock: number;
  qty_reserved: number;
  qty_ordered: number;
  min_quantity: number;
  reorder_quantity: number | null;
  unit: string | null;
  facility_id: string | null;
  zone_id: string | null;
  image_url: string | null;
  notes: string | null;
  maintainx_id: string | null;
  vendor_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  updated_by_name: string | null;
  // Computed
  qty_available?: number;
  is_low_stock?: boolean;
  facility_name?: string;
  zone_name?: string;
}

export interface CreatePartInput {
  name: string;
  description?: string;
  part_number?: string;
  barcode?: string;
  unit_cost?: number;
  qty_in_stock?: number;
  min_quantity?: number;
  reorder_quantity?: number;
  unit?: string;
  facility_id?: string | null;
  zone_id?: string | null;
  vendor_id?: string | null;
  notes?: string;
}

export interface UpdatePartInput {
  name?: string;
  description?: string | null;
  part_number?: string | null;
  barcode?: string | null;
  unit_cost?: number | null;
  qty_in_stock?: number;
  qty_reserved?: number;
  qty_ordered?: number;
  min_quantity?: number;
  reorder_quantity?: number | null;
  unit?: string | null;
  facility_id?: string | null;
  zone_id?: string | null;
  vendor_id?: string | null;
  image_url?: string | null;
  notes?: string | null;
  updated_by_name?: string | null;
}

// ── Queries ─────────────────────────────────────────────────────────────

export function useParts(filters?: {
  search?: string;
  facility_id?: string;
  low_stock_only?: boolean;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["parts", filters],
    queryFn: async () => {
      let query = supabase
        .from("part" as any)
        .select("*")
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (filters?.search) query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      if (filters?.facility_id) query = query.eq("facility_id", filters.facility_id);

      const { data, error } = await query;
      if (error) throw error;

      let parts = (data || []) as Part[];

      // Compute derived fields
      parts = parts.map((p) => ({
        ...p,
        qty_available: p.qty_in_stock - p.qty_reserved,
        is_low_stock: p.qty_in_stock <= p.min_quantity && p.min_quantity > 0,
      }));

      if (filters?.low_stock_only) {
        parts = parts.filter((p) => p.is_low_stock);
      }

      return parts;
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function usePart(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["parts", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      const p = data as Part;
      return {
        ...p,
        qty_available: p.qty_in_stock - p.qty_reserved,
        is_low_stock: p.qty_in_stock <= p.min_quantity && p.min_quantity > 0,
      };
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

export function useLowStockParts() {
  return useParts({ low_stock_only: true });
}

export function usePartVendors(partId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["part-vendors", partId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_vendor" as any)
        .select("*, vendor:vendor_id(*)")
        .eq("part_id", partId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!partId && isAuthenticated && !authLoading,
  });
}

export function useUpsertPartVendorNumber() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ partId, vendorId, vendorPartNumber }: { partId: string; vendorId: string; vendorPartNumber: string | null }) => {
      const org_id = await (async () => {
        const { data: { user } } = await supabase.auth.getUser();
        const { data } = await supabase.from("user_account").select("org_id").eq("external_auth_user", user!.id).single();
        return data!.org_id as string;
      })();

      const { error } = await supabase
        .from("part_vendor" as any)
        .upsert(
          { part_id: partId, vendor_id: vendorId, vendor_part_number: vendorPartNumber, org_id },
          { onConflict: "part_id,vendor_id" }
        );
      if (error) throw error;
    },
    onSuccess: (_data, { partId }) => {
      qc.invalidateQueries({ queryKey: ["part-vendors", partId] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function usePartAssets(partId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["part-assets", partId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_part" as any)
        .select("*, asset:asset_id(*)")
        .eq("part_id", partId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!partId && isAuthenticated && !authLoading,
  });
}

// ── Part Components (assembly BOM) ───────────────────────────────────────

export interface PartComponent {
  id: string;
  assembly_part_id: string;
  component_part_id: string;
  quantity: number;
  component_name: string;
  component_part_number: string | null;
  component_image_url: string | null;
  component_unit: string | null;
}

export function usePartComponents(partId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["part-components", partId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_component" as any)
        .select("*, component:component_part_id(id, name, part_number, image_url, unit)")
        .eq("assembly_part_id", partId!);
      if (error) throw error;
      return (data || []).map((row: any) => ({
        id: row.id,
        assembly_part_id: row.assembly_part_id,
        component_part_id: row.component_part_id,
        quantity: row.quantity,
        component_name: row.component.name,
        component_part_number: row.component.part_number,
        component_image_url: row.component.image_url,
        component_unit: row.component.unit,
      })) as PartComponent[];
    },
    enabled: !!partId && isAuthenticated && !authLoading,
  });
}

export function usePartUsedInAssemblies(partId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["part-used-in", partId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_component" as any)
        .select("*, assembly:assembly_part_id(id, name, part_number)")
        .eq("component_part_id", partId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!partId && isAuthenticated && !authLoading,
  });
}

export function useAddPartComponent() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ assemblyPartId, componentPartId, quantity }: { assemblyPartId: string; componentPartId: string; quantity: number }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase.from("user_account").select("org_id").eq("external_auth_user", user!.id).single();
      const { error } = await supabase
        .from("part_component" as any)
        .insert({ assembly_part_id: assemblyPartId, component_part_id: componentPartId, quantity, org_id: ua!.org_id });
      if (error) throw error;
    },
    onSuccess: (_data, { assemblyPartId }) => {
      qc.invalidateQueries({ queryKey: ["part-components", assemblyPartId] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useUpdatePartComponent() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, assemblyPartId, quantity }: { id: string; assemblyPartId: string; quantity: number }) => {
      const { error } = await supabase
        .from("part_component" as any)
        .update({ quantity })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, { assemblyPartId }) => {
      qc.invalidateQueries({ queryKey: ["part-components", assemblyPartId] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useRemovePartComponent() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, assemblyPartId }: { id: string; assemblyPartId: string }) => {
      const { error } = await supabase
        .from("part_component" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, { assemblyPartId }) => {
      qc.invalidateQueries({ queryKey: ["part-components", assemblyPartId] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreatePart() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePartInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("part" as any)
        .insert([{
          org_id: userData.org_id,
          name: input.name,
          description: input.description || null,
          part_number: input.part_number || null,
          barcode: input.barcode || null,
          unit_cost: input.unit_cost || null,
          qty_in_stock: input.qty_in_stock || 0,
          min_quantity: input.min_quantity || 0,
          unit: input.unit || "each",
          facility_id: input.facility_id || null,
          zone_id: input.zone_id || null,
          vendor_id: input.vendor_id || null,
          notes: input.notes || null,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Part;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parts"] });
      toast({ title: "Part created", description: "The part has been added to inventory." });
    },
    onError: (error: Error) => {
      toast({ title: "Error creating part", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdatePart() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePartInput }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase
        .from("user_account" as any)
        .select("display_name")
        .eq("external_auth_user", user!.id)
        .single();
      const updatedByName = (ua as any)?.display_name || user?.email || null;

      const { data, error } = await supabase
        .from("part" as any)
        .update({ ...input, updated_by_name: updatedByName })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Part;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parts"] });
      toast({ title: "Part updated", description: "The part has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating part", description: error.message, variant: "destructive" });
    },
  });
}

export function useDuplicatePart() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (part: Part) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase
        .from("user_account" as any)
        .select("org_id, display_name")
        .eq("external_auth_user", user!.id)
        .single();

      const { data, error } = await supabase
        .from("part" as any)
        .insert({
          org_id: (ua as any).org_id,
          name: `${part.name} (Copy)`,
          description: part.description,
          part_number: part.part_number,
          unit_cost: part.unit_cost,
          qty_in_stock: 0,
          min_quantity: part.min_quantity,
          reorder_quantity: part.reorder_quantity,
          unit: part.unit,
          vendor_id: part.vendor_id,
          notes: part.notes,
          updated_by_name: (ua as any)?.display_name || user?.email || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Part;
    },
    onSuccess: (newPart) => {
      queryClient.invalidateQueries({ queryKey: ["parts"] });
      toast({ title: "Part duplicated", description: `"${newPart.name}" created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error duplicating part", description: error.message, variant: "destructive" });
    },
  });
}

export function useArchivePart() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("part" as any)
        .update({ is_active: false })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Part;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parts"] });
      toast({ title: "Part archived", description: "The part has been archived." });
    },
    onError: (error: Error) => {
      toast({ title: "Error archiving part", description: error.message, variant: "destructive" });
    },
  });
}
